import { computed, ref } from "vue";
import { defineStore } from "pinia";
import * as customMapApi from "@/api/customMapApi.js";
import { backendEnabled } from "@/services/backendMode.js";

const STORAGE_KEY = "bricksbeater_custom_star_maps_v3";
const LEGACY_STORAGE_KEY = "bricksbeater_custom_star_map_v1";
const REMOTE_MIGRATION_KEY = "bricksbeater_workshop_remote_migration_v1";
const REMOTE_UI_KEY = "bricksbeater_workshop_remote_ui_v1";
const VISIBILITIES = new Set(["private", "share", "public"]);
const MIN_NODE_COUNT = 1;
const MAX_NODE_COUNT = 15;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || min));
}

function makeId(prefix = "map") {
  if (globalThis.crypto?.randomUUID) return `${prefix}-${globalThis.crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeLevelRows(rows) {
  if (!Array.isArray(rows)) return null;
  const normalized = rows.map(row => String(row ?? ""));
  if (normalized.length !== 31 || normalized.some(row => row.length !== 51)) return null;
  return normalized;
}


function normalizeCustomColors(raw) {
  if (!raw || typeof raw !== "object") return {};
  const allowedSymbols = new Set(["1", "2", "3", "4", "5", "6", "7", "8"]);
  const normalized = {};
  for (const [symbol, value] of Object.entries(raw)) {
    const color = String(value || "").trim().toLowerCase();
    if (allowedSymbols.has(symbol) && /^#[0-9a-f]{6}$/i.test(color)) normalized[symbol] = color;
  }
  return normalized;
}


function normalizePaletteSlots(raw) {
  const defaults = ["C", "B", "G", "Y", "R", "W", "#", " "];
  if (!Array.isArray(raw) || raw.length !== 8) return defaults;
  const allowed = new Set(["C", "B", "G", "Y", "R", "W", "#", " ", "1", "2", "3", "4", "5", "6", "7", "8"]);
  const normalized = raw.map((symbol, index) => allowed.has(String(symbol)) ? String(symbol) : defaults[index]);
  // 工具位保持固定，避免自定义颜色把边界砖和橡皮擦挤掉。
  normalized[6] = "#";
  normalized[7] = " ";
  return normalized;
}

function normalizeLevelData(raw) {
  if (!raw || typeof raw !== "object") return null;
  const rows = normalizeLevelRows(raw.rows);
  if (!rows) return null;
  return {
    rows,
    customColors: normalizeCustomColors(raw.customColors),
    paletteSlots: normalizePaletteSlots(raw.paletteSlots),
    sourceImageDataUrl: raw.sourceImageDataUrl ? String(raw.sourceImageDataUrl) : null,
    updatedAt: Number(raw.updatedAt) || Date.now(),
    createdAt: Number(raw.createdAt) || Number(raw.updatedAt) || Date.now()
  };
}

function normalizeNode(node, index) {
  const level = index + 1;
  return {
    id: String(node?.id || `node-${level}`),
    level,
    x: clamp(node?.x, 0.02, 0.98),
    y: clamp(node?.y, 0.02, 0.98),
    color: /^#[0-9a-f]{6}$/i.test(String(node?.color || "")) ? String(node.color) : "#7ff8ef",
    levelData: normalizeLevelData(node?.levelData)
  };
}

function normalizeLitLevels(raw, nodeCount) {
  if (!raw || typeof raw !== "object") return {};
  const next = {};
  for (const [key, value] of Object.entries(raw)) {
    const level = Number(key);
    if (value && Number.isInteger(level) && level >= 1 && level <= nodeCount) next[String(level)] = true;
  }
  return next;
}

function normalizeMap(raw) {
  if (!raw || typeof raw !== "object" || !raw.imageDataUrl || !Array.isArray(raw.nodes)) return null;
  const requestedCount = clamp(raw.nodeCount || raw.nodes.length, MIN_NODE_COUNT, MAX_NODE_COUNT);
  const sourceNodes = raw.nodes.slice(0, requestedCount);
  if (!sourceNodes.length) return null;
  const nodes = sourceNodes.map(normalizeNode);
  const id = String(raw.id || makeId());
  return {
    version: 3,
    id,
    name: String(raw.name || "My Star Map").slice(0, 48),
    description: String(raw.description || "").slice(0, 180),
    imageDataUrl: String(raw.imageDataUrl),
    imageWidth: Math.max(1, Number(raw.imageWidth) || 1),
    imageHeight: Math.max(1, Number(raw.imageHeight) || 1),
    aspectRatio: clamp(raw.aspectRatio || 1, 0.3, 3),
    nodeCount: nodes.length,
    visibility: VISIBILITIES.has(raw.visibility) ? raw.visibility : "private",
    moderationStatus: String(raw.moderationStatus || "pending"),
    moderationReason: String(raw.moderationReason || ""),
    reportCount: Math.max(0, Number(raw.reportCount) || 0),
    shareCode: raw.shareCode ? String(raw.shareCode) : null,
    shareUrl: raw.shareUrl ? String(raw.shareUrl) : null,
    nodes,
    builtLevels: Array.isArray(raw.builtLevels)
      ? raw.builtLevels
          .map(Number)
          .filter(level =>
            Number.isInteger(level)
            && level >= 1
            && level <= nodes.length
          )
      : [],
    litLevels: normalizeLitLevels(raw.litLevels, nodes.length),
    createdAt: Number(raw.createdAt) || Date.now(),
    updatedAt: Number(raw.updatedAt) || Date.now()
  };
}

function safeParseStorage(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function readRemoteUi() {
  const raw = safeParseStorage(REMOTE_UI_KEY);
  return {
    activeMapId: raw?.activeMapId ? String(raw.activeMapId) : null
  };
}

function persistRemoteUi(activeMapId = null) {
  localStorage.setItem(REMOTE_UI_KEY, JSON.stringify({
    version: 1,
    activeMapId: activeMapId ? String(activeMapId) : null
  }));
}

function readMigrationState() {
  const raw = safeParseStorage(REMOTE_MIGRATION_KEY);
  return {
    version: 1,
    completed: Boolean(raw?.completed),
    mapIds: raw?.mapIds && typeof raw.mapIds === "object" ? { ...raw.mapIds } : {},
    mapAssets: raw?.mapAssets && typeof raw.mapAssets === "object" ? { ...raw.mapAssets } : {},
    sourceAssets: raw?.sourceAssets && typeof raw.sourceAssets === "object" ? { ...raw.sourceAssets } : {},
    migratedLevels: raw?.migratedLevels && typeof raw.migratedLevels === "object"
      ? Object.fromEntries(
          Object.entries(raw.migratedLevels).map(([mapId, levels]) => [
            String(mapId),
            Array.isArray(levels) ? levels.map(Number).filter(Number.isInteger) : []
          ])
        )
      : {}
  };
}

function writeMigrationState(state) {
  localStorage.setItem(REMOTE_MIGRATION_KEY, JSON.stringify({
    version: 1,
    completed: Boolean(state.completed),
    mapIds: state.mapIds || {},
    mapAssets: state.mapAssets || {},
    sourceAssets: state.sourceAssets || {},
    migratedLevels: state.migratedLevels || {}
  }));
}

function readLegacyCollectionForMigration() {
  const current = safeParseStorage(STORAGE_KEY);
  if (current && Array.isArray(current.maps)) {
    const maps = current.maps.map(normalizeMap).filter(Boolean);
    return {
      maps,
      activeMapId: current.activeMapId ? String(current.activeMapId) : null,
      challengeMapId: current.challengeMapId ? String(current.challengeMapId) : null
    };
  }

  const legacy = normalizeMap(safeParseStorage(LEGACY_STORAGE_KEY));
  return legacy
    ? { maps: [legacy], activeMapId: legacy.id, challengeMapId: null }
    : { maps: [], activeMapId: null, challengeMapId: null };
}

async function dataUrlToBlob(dataUrl) {
  const value = String(dataUrl || "");
  if (!value.startsWith("data:")) return null;
  const response = await fetch(value);
  return response.blob();
}

function migrationLevelKey(localMapId, level) {
  return `${String(localMapId)}:${Number(level)}`;
}

function loadCollection() {
  if (backendEnabled && readMigrationState().completed) {
    return {
      maps: [],
      activeMapId: null,
      challengeMapId: null
    };
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (parsed && Array.isArray(parsed.maps)) {
      const maps = parsed.maps.map(normalizeMap).filter(Boolean);
      const activeMapId = maps.some(map => map.id === parsed.activeMapId)
        ? parsed.activeMapId
        : (maps[0]?.id || null);
      const challengeMapId = maps.some(map => map.id === parsed.challengeMapId)
        ? parsed.challengeMapId
        : null;
      return { maps, activeMapId, challengeMapId };
    }
  } catch {
    // fall through to legacy migration
  }

  try {
    const legacy = normalizeMap(JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || "null"));
    if (legacy) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return { maps: [legacy], activeMapId: legacy.id, challengeMapId: null };
    }
  } catch {
    // Ignore broken legacy payloads.
  }
  return { maps: [], activeMapId: null, challengeMapId: null };
}

function persist(collection) {
  if (backendEnabled) {
    persistRemoteUi(collection?.activeMapId || null);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(collection));
}

function normalizeGameAssetUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;

  const marker = "/game-assets";
  const index = raw.indexOf(marker);
  if (index >= 0) {
    return raw.slice(index);
  }

  if (raw === "game-assets") return "/game-assets";
  if (raw.startsWith("game-assets/")) return `/${raw}`;

  return raw;
}

function normalizeRemoteMap(raw, previous = null) {
  if (!raw || typeof raw !== "object" || !raw.id || !Array.isArray(raw.nodes)) return null;
  const previousNodes = Array.isArray(previous?.nodes) ? previous.nodes : [];
  const nodes = raw.nodes.map((node, index) => {
    const oldNode = previousNodes.find(item => item.level === Number(node?.level))
      || previousNodes[index]
      || null;
    return normalizeNode({
      ...node,
      levelData: oldNode?.levelData || null
    }, index);
  });
  const litLevels = previous?.litLevels || {};
  return {
    version: 4,
    id: String(raw.id),
    name: String(raw.name || "").slice(0, 48),
    description: String(raw.description || "").slice(0, 180),
    author: String(raw.author || previous?.author || ""),
    authorUserId: raw.authorUserId != null
      ? Number(raw.authorUserId)
      : (previous?.authorUserId ?? null),
    authorAvatarUrl: String(raw.authorAvatarUrl || previous?.authorAvatarUrl || ""),
    imageDataUrl: normalizeGameAssetUrl(raw.imageUrl || raw.image || ""),
    imageStorageKey: raw.imageStorageKey ? String(raw.imageStorageKey) : null,
    imageWidth: Math.max(1, Number(raw.imageWidth) || Number(previous?.imageWidth) || 1),
    imageHeight: Math.max(1, Number(raw.imageHeight) || Number(previous?.imageHeight) || 1),
    aspectRatio: clamp(raw.aspectRatio || previous?.aspectRatio || 1, 0.3, 3),
    nodeCount: nodes.length,
    visibility: VISIBILITIES.has(raw.visibility) ? raw.visibility : "private",
    moderationStatus: String(raw.moderationStatus || previous?.moderationStatus || "pending"),
    moderationReason: String(raw.moderationReason || ""),
    reportCount: Math.max(0, Number(raw.reportCount) || 0),
    shareCode: raw.shareCode ? String(raw.shareCode) : null,
    shareUrl: raw.shareUrl ? String(raw.shareUrl) : (previous?.shareUrl || null),
    sharePath: raw.sharePath ? String(raw.sharePath) : (previous?.sharePath || null),
    accessShareCode: raw.accessShareCode
      ? String(raw.accessShareCode)
      : (previous?.accessShareCode || null),
    accessMode: raw.accessMode
      ? String(raw.accessMode)
      : (previous?.accessMode || "owner"),
    builtLevels: Array.isArray(raw.builtLevels)
      ? raw.builtLevels.map(Number).filter(Number.isInteger)
      : (previous?.builtLevels || []),
    plays: Math.max(0, Number(raw.plays) || 0),
    likes: Math.max(0, Number(raw.likes) || 0),
    nodes,
    litLevels: normalizeLitLevels(litLevels, nodes.length),
    createdAt: raw.createdAt || previous?.createdAt || null,
    updatedAt: raw.updatedAt || previous?.updatedAt || null
  };
}

function levelDataFromRemote(raw, previous = null) {
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.rows)) return null;
  const rows = normalizeLevelRows(raw.rows);
  if (!rows) return null;
  return {
    rows,
    customColors: normalizeCustomColors(raw.customColors),
    paletteSlots: normalizePaletteSlots(raw.paletteSlots),
    sourceImageDataUrl: raw.sourceImageUrl ? String(raw.sourceImageUrl) : null,
    sourceImageStorageKey: raw.sourceImageStorageKey
      ? String(raw.sourceImageStorageKey)
      : null,
    createdAt: raw.createdAt || previous?.createdAt || null,
    updatedAt: raw.updatedAt || previous?.updatedAt || null
  };
}

function defaultLevelRows() {
  const rows = Array.from({ length: 31 }, () => Array.from({ length: 51 }, () => " "));
  for (let x = 0; x < 51; x += 1) rows[0][x] = "#";
  return rows.map(row => row.join(""));
}

export const useCustomStarMapStore = defineStore("customStarMap", () => {
  const initial = loadCollection();
  const remoteUi = readRemoteUi();
  const maps = ref(initial.maps);
  const activeMapId = ref(initial.activeMapId);
  const remotePreferredActiveMapId = ref(remoteUi.activeMapId);
  // 当前“闯关地图”与编辑中的 activeMap 分开保存。null 代表系统默认地图。
  const challengeMapId = ref(initial.challengeMapId);
  const remoteLoading = ref(false);
  const remoteReady = ref(false);
  const remoteError = ref(null);

  const activeMap = computed(() => maps.value.find(map => map.id === activeMapId.value) || null);
  const challengeMap = computed(() => maps.value.find(map => map.id === challengeMapId.value) || null);
  const hasMap = computed(() => maps.value.length > 0);
  const levelCount = computed(() => activeMap.value?.nodes?.length || 0);
  const litCount = computed(() => {
    const map = activeMap.value;
    if (!map) return 0;
    return map.nodes.reduce((count, node) => count + (map.litLevels[String(node.level)] ? 1 : 0), 0);
  });

  function commit(
    nextMaps = maps.value,
    nextActiveId = activeMapId.value,
    nextChallengeMapId = challengeMapId.value
  ) {
    maps.value = nextMaps;
    activeMapId.value = nextMaps.some(map => map.id === nextActiveId)
      ? nextActiveId
      : (nextMaps[0]?.id || null);
    challengeMapId.value = nextMaps.some(map => map.id === nextChallengeMapId)
      ? nextChallengeMapId
      : null;
    if (backendEnabled) {
      remotePreferredActiveMapId.value = activeMapId.value;
    }
    persist({
      version: 3,
      maps: maps.value,
      activeMapId: activeMapId.value,
      challengeMapId: challengeMapId.value
    });
  }

  function refresh() {
    if (backendEnabled) return maps.value;
    const next = loadCollection();
    maps.value = next.maps;
    activeMapId.value = next.activeMapId;
    challengeMapId.value = next.challengeMapId;
    return maps.value;
  }

  function getMap(mapId) {
    return maps.value.find(map => map.id === String(mapId)) || null;
  }

  function getNode(mapId, level) {
    const map = getMap(mapId);
    return map?.nodes?.find(node => node.level === Number(level)) || null;
  }

  function saveMap(payload, { mapId = null, resetProgress = false, resetLevels = false } = {}) {
    const existing = mapId ? getMap(mapId) : null;
    const incomingNodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
    const nodes = incomingNodes.map((node, index) => {
      const oldNode = existing?.nodes?.[index];
      return {
        ...node,
        level: index + 1,
        id: node?.id || oldNode?.id || `node-${index + 1}`,
        levelData: resetLevels ? null : (node?.levelData ?? oldNode?.levelData ?? null)
      };
    });

    const normalized = normalizeMap({
      ...existing,
      ...payload,
      id: existing?.id || payload?.id || makeId(),
      nodes,
      litLevels: resetProgress ? {} : (payload?.litLevels ?? existing?.litLevels ?? {}),
      createdAt: existing?.createdAt || Date.now(),
      updatedAt: Date.now()
    });
    if (!normalized) throw new Error("Invalid star map.");

    const index = maps.value.findIndex(map => map.id === normalized.id);
    const next = [...maps.value];
    if (index >= 0) next.splice(index, 1, normalized);
    else next.unshift(normalized);
    commit(next, normalized.id);
    return normalized;
  }

  function setActiveMap(mapId) {
    if (!getMap(mapId)) return false;
    commit(maps.value, String(mapId));
    return true;
  }

  function setChallengeMap(mapId = null) {
    // null = 使用系统默认关卡。
    if (mapId === null || mapId === undefined || mapId === "") {
      commit(maps.value, activeMapId.value, null);
      return true;
    }
    const id = String(mapId);
    if (!getMap(id)) return false;
    commit(maps.value, activeMapId.value, id);
    return true;
  }

  function getCurrentChallengeLevel(mapId = challengeMapId.value) {
    const map = getMap(mapId);
    if (!map?.nodes?.length) return 1;
    const firstUnlit = map.nodes.find(node => !map.litLevels?.[String(node.level)]);
    return firstUnlit?.level || map.nodes.length;
  }

  function deleteMap(mapId) {
    const id = String(mapId);
    commit(
      maps.value.filter(map => map.id !== id),
      activeMapId.value === id ? null : activeMapId.value,
      challengeMapId.value === id ? null : challengeMapId.value
    );
  }

  function clearMap() {
    if (activeMapId.value) deleteMap(activeMapId.value);
  }

  function updateVisibility(mapId, visibility) {
    if (!VISIBILITIES.has(visibility)) return null;
    const map = getMap(mapId);
    if (!map) return null;
    return saveMap({ ...map, visibility }, { mapId: map.id });
  }

  function setShareInfo(mapId, { shareCode = null, shareUrl = null } = {}) {
    const map = getMap(mapId);
    if (!map) return null;
    return saveMap({ ...map, shareCode, shareUrl }, { mapId: map.id });
  }

  function saveLevel(mapId, level, rows, options = {}) {
    const map = getMap(mapId);
    if (!map) throw new Error("Map not found.");
    const index = map.nodes.findIndex(node => node.level === Number(level));
    if (index < 0) throw new Error("Level node not found.");
    const normalizedRows = normalizeLevelRows(rows);
    if (!normalizedRows) throw new Error("Invalid custom level rows.");
    const now = Date.now();
    const nodes = map.nodes.map((node, nodeIndex) => nodeIndex === index ? {
      ...node,
      levelData: {
        rows: normalizedRows,
        customColors: normalizeCustomColors(options.customColors ?? node.levelData?.customColors),
        paletteSlots: normalizePaletteSlots(options.paletteSlots ?? node.levelData?.paletteSlots),
        sourceImageDataUrl: options.sourceImageDataUrl !== undefined
          ? (options.sourceImageDataUrl ? String(options.sourceImageDataUrl) : null)
          : (node.levelData?.sourceImageDataUrl || null),
        createdAt: node.levelData?.createdAt || now,
        updatedAt: now
      }
    } : node);
    return saveMap({ ...map, nodes }, { mapId: map.id });
  }

  function clearLevel(mapId, level) {
    const map = getMap(mapId);
    if (!map) return null;
    const levelNo = Number(level);
    const nodes = map.nodes.map(node => node.level === levelNo ? { ...node, levelData: null } : node);
    const litLevels = { ...map.litLevels };
    delete litLevels[String(levelNo)];
    const builtLevels = Array.isArray(map.builtLevels)
      ? map.builtLevels.map(Number).filter(item => item !== levelNo)
      : [];
    return saveMap({ ...map, nodes, litLevels, builtLevels }, { mapId: map.id });
  }

  function isLevelBuilt(mapId, level) {
    const map = getMap(mapId);
    if (!map) return false;
    const levelNo = Number(level);
    if (getNode(mapId, levelNo)?.levelData?.rows) return true;
    return Array.isArray(map.builtLevels)
      && map.builtLevels.map(Number).includes(levelNo);
  }

  function isLit(level, mapId = activeMapId.value) {
    return Boolean(getMap(mapId)?.litLevels?.[String(level)]);
  }

  function markLit(level, mapId = activeMapId.value) {
    const map = getMap(mapId);
    if (!map || !isLevelBuilt(map.id, level)) return null;
    const key = String(Number(level));
    if (map.litLevels[key]) return map;
    return saveMap({ ...map, litLevels: { ...map.litLevels, [key]: true } }, { mapId: map.id });
  }

  function resetLighting(mapId = activeMapId.value) {
    const map = getMap(mapId);
    if (!map) return null;
    return saveMap({ ...map, litLevels: {} }, { mapId: map.id });
  }


  function replaceCachedMap(raw) {
    const id = String(raw?.id || "");
    if (!id) return null;
    const previous = getMap(id);
    const normalized = normalizeRemoteMap(raw, previous);
    if (!normalized) return null;
    const next = [...maps.value];
    const index = next.findIndex(map => map.id === id);
    if (index >= 0) next.splice(index, 1, normalized);
    else next.unshift(normalized);
    commit(next, activeMapId.value || normalized.id, challengeMapId.value);
    return normalized;
  }

  function removeCachedMap(mapId) {
    const id = String(mapId);
    commit(
      maps.value.filter(map => map.id !== id),
      activeMapId.value === id ? null : activeMapId.value,
      challengeMapId.value === id ? null : challengeMapId.value
    );
  }

  async function ensureRemoteMigration() {
    if (!backendEnabled) return { migrated: 0, completed: true };

    const state = readMigrationState();
    if (state.completed) return { migrated: 0, completed: true };

    const legacy = readLegacyCollectionForMigration();
    if (!legacy.maps.length) {
      state.completed = true;
      writeMigrationState(state);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return { migrated: 0, completed: true };
    }

    let migrated = 0;

    for (const localMap of legacy.maps) {
      const localId = String(localMap.id);
      let remoteId = state.mapIds[localId] ? String(state.mapIds[localId]) : "";

      if (!remoteId) {
        let mapAsset = state.mapAssets[localId] || null;

        if (!mapAsset) {
          const backgroundBlob = await dataUrlToBlob(localMap.imageDataUrl);
          if (backgroundBlob) {
            const uploaded = await customMapApi.uploadImage(backgroundBlob);
            mapAsset = {
              imageUrl: String(uploaded?.imageUrl || ""),
              imageStorageKey: uploaded?.storageKey ? String(uploaded.storageKey) : null
            };
          } else {
            mapAsset = {
              imageUrl: String(localMap.imageDataUrl || ""),
              imageStorageKey: null
            };
          }

          if (!mapAsset.imageUrl) {
            throw new Error(`Unable to migrate workshop map image: ${localId}`);
          }

          state.mapAssets[localId] = mapAsset;
          writeMigrationState(state);
        }

        const created = await customMapApi.createMap({
          name: localMap.name,
          description: localMap.description || "",
          imageUrl: mapAsset.imageUrl,
          ...(mapAsset.imageStorageKey ? { imageStorageKey: mapAsset.imageStorageKey } : {}),
          imageWidth: localMap.imageWidth,
          imageHeight: localMap.imageHeight,
          aspectRatio: localMap.aspectRatio,
          nodeCount: localMap.nodes.length,
          visibility: localMap.visibility,
          nodes: localMap.nodes.map(node => ({
            id: String(node.id),
            level: Number(node.level),
            x: Number(node.x),
            y: Number(node.y),
            color: String(node.color || "#7ff8ef")
          }))
        });

        remoteId = String(created?.id || "");
        if (!remoteId) {
          throw new Error(`Workshop map migration returned no map id: ${localId}`);
        }

        state.mapIds[localId] = remoteId;
        state.migratedLevels[localId] = state.migratedLevels[localId] || [];
        writeMigrationState(state);
        migrated += 1;
      }

      const doneLevels = new Set(
        Array.isArray(state.migratedLevels[localId])
          ? state.migratedLevels[localId].map(Number)
          : []
      );

      for (const node of localMap.nodes) {
        const level = Number(node.level);
        const levelData = node.levelData;
        if (!levelData?.rows || doneLevels.has(level)) continue;

        const sourceKey = migrationLevelKey(localId, level);
        let sourceAsset = state.sourceAssets[sourceKey] || null;

        if (!sourceAsset && levelData.sourceImageDataUrl) {
          const sourceBlob = await dataUrlToBlob(levelData.sourceImageDataUrl);
          if (sourceBlob) {
            const uploaded = await customMapApi.uploadImage(sourceBlob);
            sourceAsset = {
              sourceImageUrl: String(uploaded?.imageUrl || ""),
              sourceImageStorageKey: uploaded?.storageKey
                ? String(uploaded.storageKey)
                : null
            };
          } else {
            sourceAsset = {
              sourceImageUrl: String(levelData.sourceImageDataUrl),
              sourceImageStorageKey: null
            };
          }
          state.sourceAssets[sourceKey] = sourceAsset;
          writeMigrationState(state);
        }

        await customMapApi.saveLevel(remoteId, level, {
          rows: levelData.rows,
          customColors: levelData.customColors || {},
          paletteSlots: levelData.paletteSlots || normalizePaletteSlots(null),
          ...(sourceAsset?.sourceImageUrl
            ? { sourceImageUrl: sourceAsset.sourceImageUrl }
            : {}),
          ...(sourceAsset?.sourceImageStorageKey
            ? { sourceImageStorageKey: sourceAsset.sourceImageStorageKey }
            : {})
        });

        doneLevels.add(level);
        state.migratedLevels[localId] = [...doneLevels].sort((a, b) => a - b);
        writeMigrationState(state);
      }
    }

    if (legacy.challengeMapId) {
      const remoteChallengeId = state.mapIds[String(legacy.challengeMapId)] || null;
      if (remoteChallengeId) {
        await customMapApi.setChallengeMap(remoteChallengeId);
      }
    }

    const preferredLocalId = legacy.activeMapId ? String(legacy.activeMapId) : "";
    const preferredRemoteId = preferredLocalId
      ? state.mapIds[preferredLocalId] || null
      : null;

    if (preferredRemoteId) {
      remotePreferredActiveMapId.value = String(preferredRemoteId);
      persistRemoteUi(remotePreferredActiveMapId.value);
    }

    state.completed = true;
    writeMigrationState(state);

    // Only remove the old authoritative map blobs after every map/level operation succeeded.
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);

    return { migrated, completed: true };
  }

  async function loadRemoteMaps() {
    if (!backendEnabled) {
      remoteReady.value = false;
      return maps.value;
    }
    remoteLoading.value = true;
    remoteError.value = null;
    try {
      await ensureRemoteMigration();

      const response = await customMapApi.listMyMaps();
      const incoming = Array.isArray(response?.maps) ? response.maps : [];
      const next = incoming
        .map(raw => normalizeRemoteMap(raw, getMap(raw?.id)))
        .filter(Boolean);

      const preferredId = remotePreferredActiveMapId.value;
      const nextActiveId = preferredId && next.some(map => map.id === preferredId)
        ? preferredId
        : (activeMapId.value && next.some(map => map.id === activeMapId.value)
          ? activeMapId.value
          : (next[0]?.id || null));

      commit(next, nextActiveId, challengeMapId.value);
      remoteReady.value = true;
      return maps.value;
    } catch (error) {
      remoteError.value = error;
      throw error;
    } finally {
      remoteLoading.value = false;
    }
  }

  async function loadRemoteMap(mapId) {
    if (!backendEnabled) return getMap(mapId);
    remoteError.value = null;
    try {
      const previous = getMap(mapId);
      const incoming = await customMapApi.getMap(mapId);
      return replaceCachedMap({
        ...previous,
        ...incoming,
        id: incoming?.id || previous?.id || mapId,
        imageUrl: incoming?.imageUrl || incoming?.image || previous?.imageDataUrl || "",
        nodes: Array.isArray(incoming?.nodes)
          ? incoming.nodes
          : (previous?.nodes || []),
        accessMode: "owner"
      });
    } catch (error) {
      remoteError.value = error;
      throw error;
    }
  }

  async function loadPublicRemoteMap(mapId) {
    if (!backendEnabled) return getMap(mapId);
    remoteError.value = null;
    try {
      return replaceCachedMap({
        ...(await customMapApi.getPublicMap(mapId)),
        accessMode: "public",
        accessShareCode: null
      });
    } catch (error) {
      remoteError.value = error;
      throw error;
    }
  }

  async function loadSharedRemoteMap(shareCode) {
    if (!backendEnabled) return null;
    remoteError.value = null;
    try {
      return replaceCachedMap({
        ...(await customMapApi.getSharedMap(shareCode)),
        accessMode: "shared",
        accessShareCode: String(shareCode || "")
      });
    } catch (error) {
      remoteError.value = error;
      throw error;
    }
  }

  async function uploadRemoteImage(file) {
    if (!backendEnabled) return null;
    return customMapApi.uploadImage(file);
  }

  async function createRemoteMap(payload) {
    const saved = await customMapApi.createMap(payload);
    // Some development/legacy backends return only identity and counters after
    // creation. Preserve the exact image and nodes just submitted in that case.
    return replaceCachedMap({
      ...payload,
      ...saved,
      imageUrl: saved?.imageUrl || saved?.image || payload?.imageUrl || "",
      imageStorageKey: saved?.imageStorageKey || payload?.imageStorageKey || null,
      imageWidth: saved?.imageWidth || payload?.imageWidth,
      imageHeight: saved?.imageHeight || payload?.imageHeight,
      aspectRatio: saved?.aspectRatio || payload?.aspectRatio,
      visibility: saved?.visibility || payload?.visibility,
      nodes: Array.isArray(saved?.nodes) ? saved.nodes : (payload?.nodes || [])
    });
  }

  async function updateRemoteMap(mapId, payload) {
    const saved = await customMapApi.updateMap(mapId, payload);
    const previous = getMap(mapId);
    return replaceCachedMap({
      ...previous,
      ...payload,
      ...saved,
      id: saved?.id || previous?.id || mapId,
      imageUrl: saved?.imageUrl || saved?.image || payload?.imageUrl || previous?.imageDataUrl || "",
      nodes: Array.isArray(saved?.nodes)
        ? saved.nodes
        : (Array.isArray(payload?.nodes) ? payload.nodes : (previous?.nodes || []))
    });
  }

  async function deleteRemoteMap(mapId) {
    const result = await customMapApi.deleteMap(mapId);
    removeCachedMap(mapId);
    return result;
  }

  function absoluteShareUrl(sharePath) {
    const path = String(sharePath || "").trim();
    if (!path) return "";
    try {
      return new URL(path, window.location.origin).toString();
    } catch {
      return path;
    }
  }

  async function createRemoteShareLink(mapId) {
    const result = await customMapApi.createShareLink(mapId);
    const map = getMap(mapId);
    if (map) {
      const sharePath = String(result?.sharePath || "");
      saveMap({
        ...map,
        shareCode: result?.shareCode ? String(result.shareCode) : map.shareCode,
        sharePath: sharePath || map.sharePath || null,
        shareUrl: absoluteShareUrl(sharePath) || map.shareUrl || null,
        visibility: result?.visibility || map.visibility
      }, { mapId: map.id });
    }
    return result;
  }

  async function deleteRemoteShareLink(mapId) {
    const result = await customMapApi.deleteShareLink(mapId);
    const map = getMap(mapId);
    if (map) {
      saveMap({
        ...map,
        shareCode: null,
        sharePath: null,
        shareUrl: null,
        visibility: result?.visibility || map.visibility
      }, { mapId: map.id });
    }
    return result;
  }

  async function loadRemoteLevel(mapId, level) {
    const result = await customMapApi.getLevel(mapId, level);
    const map = getMap(mapId);
    if (!map) return result;
    const nodes = map.nodes.map(node => node.level === Number(level)
      ? { ...node, levelData: levelDataFromRemote(result, node.levelData) }
      : node);
    saveMap({ ...map, nodes }, { mapId: map.id });
    return result;
  }

  function markRemoteLevelUnbuilt(mapId, level) {
    const map = getMap(mapId);
    if (!map) return null;
    const nodes = map.nodes.map(node => node.level === Number(level)
      ? { ...node, levelData: null }
      : node);
    return saveMap({ ...map, nodes }, { mapId: map.id });
  }

  async function saveRemoteLevel(mapId, level, payload) {
    const result = await customMapApi.saveLevel(mapId, level, payload);
    const map = getMap(mapId);
    if (map) {
      const levelNo = Number(level);
      const nodes = map.nodes.map(node => node.level === levelNo
        ? { ...node, levelData: levelDataFromRemote(result, node.levelData) }
        : node);
      const builtLevels = Array.from(new Set([
        ...(Array.isArray(map.builtLevels) ? map.builtLevels.map(Number) : []),
        levelNo,
      ])).sort((left, right) => left - right);
      saveMap({ ...map, nodes, builtLevels }, { mapId: map.id });
    }
    return result;
  }

  async function deleteRemoteLevel(mapId, level) {
    const result = await customMapApi.deleteLevel(mapId, level);
    clearLevel(mapId, level);
    return result;
  }

  async function loadRemoteChallengeMap() {
    if (!backendEnabled) return challengeMapId.value;
    const result = await customMapApi.getChallengeMap();
    challengeMapId.value = result?.mapId ? String(result.mapId) : null;
    persist({
      version: 3,
      maps: maps.value,
      activeMapId: activeMapId.value,
      challengeMapId: challengeMapId.value
    });
    return challengeMapId.value;
  }

  async function setRemoteChallengeMap(mapId = null) {
    if (!backendEnabled) return setChallengeMap(mapId);
    const result = await customMapApi.setChallengeMap(mapId);
    challengeMapId.value = result?.mapId ? String(result.mapId) : null;
    persist({
      version: 3,
      maps: maps.value,
      activeMapId: activeMapId.value,
      challengeMapId: challengeMapId.value
    });
    return challengeMapId.value;
  }

  async function loadRemoteProgress(mapId, options = {}) {
    if (!backendEnabled) return null;
    const result = await customMapApi.getProgress(mapId, options);
    const map = getMap(mapId);
    if (map) {
      const litLevels = Object.fromEntries(
        (Array.isArray(result?.litLevels) ? result.litLevels : [])
          .map(level => [String(Number(level)), true])
      );
      saveMap({ ...map, litLevels }, { mapId: map.id });
    }
    return result;
  }

  async function startRemotePlay(mapId, level, options = {}) {
    if (!backendEnabled) return null;
    const result = await customMapApi.startCustomLevel(mapId, level, options);
    const map = getMap(mapId);
    if (map && Array.isArray(result?.rows)) {
      const nodes = map.nodes.map(node => node.level === Number(level)
        ? { ...node, levelData: levelDataFromRemote(result, node.levelData) }
        : node);
      saveMap({ ...map, nodes }, { mapId: map.id });
    }
    return result;
  }

  async function finishRemotePlay(mapId, level, payload) {
    if (!backendEnabled) return null;
    return customMapApi.finishCustomLevel(mapId, level, payload);
  }

  return {
    maps,
    remoteLoading,
    remoteReady,
    remoteError,
    backendEnabled,
    activeMapId,
    activeMap,
    challengeMapId,
    challengeMap,
    hasMap,
    levelCount,
    litCount,
    refresh,
    getMap,
    getNode,
    saveMap,
    setActiveMap,
    setChallengeMap,
    getCurrentChallengeLevel,
    deleteMap,
    clearMap,
    updateVisibility,
    setShareInfo,
    saveLevel,
    clearLevel,
    isLevelBuilt,
    isLit,
    markLit,
    resetLighting,
    ensureRemoteMigration,
    loadRemoteMaps,
    loadRemoteMap,
    loadPublicRemoteMap,
    loadSharedRemoteMap,
    uploadRemoteImage,
    createRemoteMap,
    updateRemoteMap,
    deleteRemoteMap,
    createRemoteShareLink,
    deleteRemoteShareLink,
    loadRemoteLevel,
    markRemoteLevelUnbuilt,
    saveRemoteLevel,
    deleteRemoteLevel,
    loadRemoteChallengeMap,
    setRemoteChallengeMap,
    loadRemoteProgress,
    startRemotePlay,
    finishRemotePlay,
    defaultLevelRows,
    MIN_NODE_COUNT,
    MAX_NODE_COUNT
  };
});
